import {
  ChevronDown,
  ChevronUp,
  House,
  LayoutGrid,
  PanelLeft,
  Plus,
  SlidersHorizontal,
} from "lucide-react";
import type { ComponentType } from "react";

import { Tip } from "@/components/ui/overlays";
import { workspace } from "@/components/workspace/controller";
import { useWorkspaceStore } from "@/components/workspace/store";
import { cn } from "@/lib/utils";
import { useActivity } from "@/stores/activity";
import { useGraph } from "@/stores/graph";
import { useShell } from "@/stores/shell";
import { useToolsOverlay } from "@/stores/tools-overlay";

/** The compact-layout bottom dock — an iOS tab bar with a raised create key.
 *
 * Four destinations, two per flank, around a centred "+". It used to carry six
 * (Home, Files, Orbits, +, Inspect, Projects), which is more than a tab bar can
 * label legibly on a phone and left the "+" off centre. Orbits and Projects did
 * not earn a permanent seat: both are one tap away in the Tools grid, which now
 * has a seat of its own — and Tools is the surface that makes every other
 * feature reachable, so on a device with no keyboard it is worth far more than
 * either.
 *
 * In-flow (not fixed) so it never collides with the mobile browser chrome, with
 * a safe-area pad for the home bar. A small handle collapses it to a slim edge
 * pill (state in `stores/shell`, persisted) so a document can take the whole
 * screen when wanted. */
function DockButton({
  icon: Icon,
  label,
  active,
  badge,
  onClick,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  active?: boolean;
  badge?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        "relative flex flex-1 flex-col items-center justify-center gap-0.5 py-1.5 transition-colors",
        active ? "text-primary" : "text-muted-foreground hover:text-muted-foreground",
      )}
    >
      {/* Active rail — a primary-accent filament above the live destination. */}
      <span
        className={cn(
          "absolute inset-x-5 top-0 h-px rounded-full bg-primary transition-opacity",
          active ? "opacity-90 shadow-glow" : "opacity-0",
        )}
      />
      <Icon className="h-[18px] w-[18px]" />
      {badge && <span className="absolute right-1/4 top-1.5 h-1.5 w-1.5 rounded-full bg-primary animate-led" />}
      <span className="font-mono text-[9px] uppercase tracking-wider">{label}</span>
    </button>
  );
}

export function MobileDock() {
  const dockOpen = useShell((s) => s.dockOpen);
  const leftOpen = useShell((s) => s.leftOpen);
  const rightPane = useShell((s) => s.rightPane);
  const toolsOpen = useToolsOverlay((s) => s.open);
  const hasSelection = useGraph((s) => s.selection !== null);
  const liveCount = useActivity(
    (s) => s.trackers.filter((t) => t.status === "running" || t.status === "queued").length,
  );

  const closed = !leftOpen && rightPane === null;
  // Home is the app's landing destination on every platform now, and the
  // compact left drawer is the file tree only — without this button there is no
  // way to reach it on a phone at all.
  const homeActive = useWorkspaceStore((s) => s.activeId === "home:main") && closed;

  // Collapsed: a slim edge pill that brings the dock back. Still honours the
  // home-bar safe area so the affordance never hides under system chrome.
  if (!dockOpen) {
    return (
      <nav className="hairline-t z-30 flex shrink-0 items-center justify-center bg-card/95 pb-safe backdrop-blur">
        <Tip label="Show navigation" side="top">
          <button
            type="button"
            onClick={() => useShell.getState().toggleDock()}
            aria-label="Show navigation"
            className="grid h-7 w-24 place-items-center rounded-t-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <ChevronUp className="h-4 w-4" aria-hidden />
          </button>
        </Tip>
      </nav>
    );
  }

  return (
    <nav className="hairline-t z-30 flex shrink-0 flex-col bg-card/95 pb-safe backdrop-blur">
      {/* Collapse handle — right-aligned so it clears both the centered raised
          "+" key and the assistant launcher above the dock. */}
      <div className="flex h-4 items-center justify-end pr-2">
        <Tip label="Hide navigation" side="top">
          <button
            type="button"
            onClick={() => useShell.getState().toggleDock()}
            aria-label="Hide navigation"
            className="grid h-4 w-12 place-items-center rounded text-muted-foreground/70 transition-colors hover:bg-accent hover:text-foreground"
          >
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          </button>
        </Tip>
      </div>

      {/* Two equal flanks around a fixed centre. The "+" used to be the fourth
          child of one flat row with three flex-1 buttons on its left and two on
          its right, which put it at three fifths of the width and not in the
          middle — visible on a tablet, where the dock is wide enough to see it.
          Centring it by counting buttons is a rule nobody will remember; this
          centres it by CONSTRUCTION, and stays centred whatever each flank
          holds. */}
      <div className="flex items-stretch">
        <div className="flex flex-1 items-stretch">
          <DockButton
            icon={House}
            label="Home"
            active={homeActive}
            onClick={() => {
              useShell.getState().closeAll();
              workspace.openHome();
            }}
          />
          <DockButton
            icon={PanelLeft}
            label="Files"
            active={leftOpen}
            onClick={() => useShell.getState().toggleLeft()}
          />
        </div>

        {/* Raised primary create key — the signature action. */}
        <div className="flex w-16 shrink-0 items-center justify-center">
          <button
            onClick={() => useShell.getState().openAddSheet()}
            aria-label="Add to the atlas"
            className="grid h-12 w-12 -translate-y-3 place-items-center rounded-full bg-primary text-primary-foreground shadow-[0_10px_24px_-6px_hsl(var(--primary)/0.55)] transition-transform duration-150 active:scale-90"
          >
            <Plus className="h-5 w-5" strokeWidth={2.4} />
          </button>
        </div>

        <div className="flex flex-1 items-stretch">
          <DockButton
            icon={LayoutGrid}
            label="Tools"
            active={toolsOpen}
            onClick={() => useToolsOverlay.getState().toggleTools()}
          />
          <DockButton
            icon={SlidersHorizontal}
            label="Inspect"
            active={rightPane === "inspect"}
            badge={hasSelection || liveCount > 0}
            onClick={() => useShell.getState().toggleRight("inspect")}
          />
        </div>
      </div>
    </nav>
  );
}
